// Starts error reporting for this build. Errors are reported with `reportError`
// from `@visioni/a3s-error-reporting`.
import { consoleReporter, initErrorReporting, recordingReporter } from "@visioni/a3s-error-reporting";
import { sentryReporter } from "@visioni/a3s-error-reporting/react-native";
import { match } from "ts-pattern";
import { appVersion, environments, isProductionRelease, platform } from "@src/config/build";
import { sentryDsn } from "@src/config/runtime";
import { isE2ETest } from "@src/modules/LaunchArguments";

/**
 * The A3S directory and realm APIs in every environment (`a3s`, `a3s-int`,
 * `a3s-dev` and their subdomains). Not Auth0 (`auth.visioni.network`), S3 or cameras.
 */
const A3S_API = /^https:\/\/([^/]+\.)?a3s(-int|-dev)?\.visioni\.network(\/|$)/;

/**
 * Whether a test harness is driving the app: a Maestro build, or a browser under
 * automation (Playwright sets `navigator.webdriver`). Web E2E runs use the same
 * production bundle as users, so the build alone cannot tell them apart.
 */
const isAutomated = (): boolean => isE2ETest() || (typeof navigator !== "undefined" && navigator.webdriver === true);

/**
 * Called by `./start-error-reporting`, which each root layout imports first.
 * Automated runs keep errors in memory and development builds log them; neither
 * sends to Sentry.
 */
export function startErrorReporting(): void {
  if (isAutomated()) {
    initErrorReporting({ reporter: recordingReporter() });
    return;
  }
  if (__DEV__) {
    initErrorReporting({ reporter: consoleReporter() });
    return;
  }
  initErrorReporting({
    reporter: sentryReporter({
      dsn: sentryDsn,
      // The same on web and native, so one release groups a version's errors across platforms.
      release: `a3s-app@${appVersion ?? "unknown"}`,
      // The environment the build was configured for (`prd`, `int`, `mr`, `dev`). A native
      // build switched to another environment at runtime keeps reporting under this one.
      environment: environments.default,
      tracesSampleRate: isProductionRelease ? 0.1 : 1,
      // Native would otherwise send trace headers to every host, including Auth0, S3
      // and cameras. Web keeps the SDK's same-origin default: the APIs are on other
      // origins, and the extra headers would need CORS to allow them.
      ...(platform.kind !== "web" && { tracePropagationTargets: [A3S_API] }),
      // Native store builds share an environment (`demo` and `production` are both
      // `prd`), so the EAS build profile tells them apart. Web has no build profile.
      tags: match(platform)
        .with({ kind: "web" }, () => ({}))
        .with({ kind: "ios" }, { kind: "android" }, (cfg) => ({ build_profile: cfg.extra.environment }))
        .exhaustive(),
    }),
  });
}
